// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// Cycle-duration display helper (pure). Turns a cycle's started_at / ended_at pair into a
// human "how long did it run" string for the My-Cycles Duration column (PRD US §4–§6):
//   - finished cycle (ended_at set)  → total elapsed start→end
//   - running cycle (ended_at null)  → elapsed-so-far start→now
//   - not-yet-started cycle (no/blank/unparseable started_at) → placeholder, never a zero
//     or a wrong number (US §6)
// Same elapsed formatting as the dashboard CycleHeader vital-signs band so the list and
// dashboard agree on how a duration reads. An unparseable boundary degrades to the
// placeholder, never throws.

export const NO_DURATION = "—"

// Exported so other dashboard panels (e.g. Cycle Health "last event" age) share the same
// formatting instead of re-implementing it (issue cycle-health-panel).
export const formatElapsed = (ms: number): string => {
  const sec = Math.floor(Math.max(0, ms) / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

// `now` is injectable so a running cycle's elapsed-so-far is deterministic under test.
export const formatCycleDuration = (
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
  now: number = Date.now(),
): string => {
  const start = startedAt ? Date.parse(startedAt) : NaN
  if (!Number.isFinite(start)) return NO_DURATION
  const end = endedAt ? Date.parse(endedAt) : now
  if (!Number.isFinite(end)) return NO_DURATION
  return formatElapsed(end - start)
}

// Returns -1 for not-yet-started cycles so placeholder rows sort below any real (≥0)
// duration and group at the short end of a numeric ascending sort.
export const durationSeconds = (
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
  now: number = Date.now(),
): number => {
  const start = startedAt ? Date.parse(startedAt) : NaN
  if (!Number.isFinite(start)) return -1
  const end = endedAt ? Date.parse(endedAt) : now
  if (!Number.isFinite(end)) return -1
  return Math.floor(Math.max(0, end - start) / 1000)
}
